/**
 * Automation-draft artifact — the chat surface of the interview (TV1-2382 /
 * TV1-2340 forks 7, 7b, 7c).
 *
 * `propose_automation` and `refine_sub_goal` stream their result back as an
 * opaque `tool_result`. This module owns the two things that must NOT live in the
 * renderer: the wire type, and the narrowing that decides whether there is
 * anything worth rendering at all.
 *
 * The wire type is declared LOCALLY and deliberately. The tools live in a BACKEND
 * package (`teselly-ml-and-ai-library`) and the web-app must not import from one
 * — the same reasoning `rulePreview.ts` records for `CandidateGraph`. The shape
 * is a structural match for `AutomationDraft` there; a drift between the two
 * shows up as a payload that fails to narrow and renders as plain text, never as
 * a crash.
 *
 * ## The card is the AGREEMENT, which is why it is durable
 *
 * Unlike the rule-preview card, this one is not decoration on an answer — it is
 * the thing the seller is being asked to agree to, and the only artifact whose
 * loss costs a full round-trip through a developer. It rides on
 * `AssistantMessageAdded.block`, projects to a per-message readmodel row, and is
 * rehydrated on load. It is NOT held in memory only, and the product copy must
 * never have to say "not saved until you submit".
 *
 * ## It is her language, never a graph
 *
 * The card renders `display.when` / `display.then` — seller-language strings the
 * backend produced — and never a node graph. The compiled graph is the REVIEWER's
 * surface (fork 8). A message carries exactly ONE block, so a rule-preview card
 * and this one cannot coexist on the same turn; `compose_rule`'s description was
 * narrowed for exactly that reason.
 *
 * ## Two things the UI is obliged to say out loud
 *
 *  1. An `enumerated` binding is a SNAPSHOT. Items added to her catalogue later
 *     are NOT covered, and nothing will tell her. `bindingIsSnapshot()` below is
 *     what the renderer keys that warning off.
 *  2. The binding must be echoed back IN HER OWN WORDS before Submit
 *     (`binding.sellerQuote`). Auto-classification is an unmeasured heuristic
 *     over Spanish prose — "…de estos tres" misread as account-wide binds
 *     everything — and her reading the quote back is the only check on it.
 */

/** Block type string this artifact registers under. Single source of truth. */
export const AUTOMATION_DRAFT_BLOCK_TYPE = 'automation-draft';

/**
 * The levels a binding can be expressed at, mirroring `HIERARCHY_LEVELS` in
 * `teselly-ml-and-ai-library/src/automation/automationSpec.ts` (the enum
 * `SubGoalBindingSchema.hierarchyLevel` is built from, and the one the submit
 * endpoint's generated body type carries verbatim). Declared as a union rather
 * than `string` because the submit body is checked against the generated
 * operation type at the call site, and that check is the ONLY gate on the wire
 * shape — a `string` here would silently defeat it.
 */
export const AUTOMATION_DRAFT_HIERARCHY_LEVELS = [
  'item',
  'warehouse',
  'publication',
  'channel',
  'integrationUserId',
  'account'
] as const;

export type AutomationDraftHierarchyLevel = typeof AUTOMATION_DRAFT_HIERARCHY_LEVELS[number];

export type AutomationDraftBinding = {
  kind: 'account-wide' | 'enumerated';
  hierarchyLevel: AutomationDraftHierarchyLevel;
  hierarchyEntityIds: string[];
  sellerQuote: string;
  label?: string;
};

export type AutomationDraftClarification = {
  id: string;
  question: string;
  answer?: string;
};

/**
 * A **Resolved Reference** (TV1-2453) — field-for-field with
 * `ResolvedReferenceSchema` in `teselly-domain/src/automation/resolvedReference.types.ts`.
 *
 * 🚨 This client READS it for display and otherwise treats it as opaque: the
 * server's `hashAutomationDraft` covers `references` and Submit re-hashes the
 * draft it receives, so the object must reach the POST exactly as it arrived.
 * `entities[].fields` in particular is display-only here (`nameUnverified`) and
 * never authoritative.
 */
export const AUTOMATION_DRAFT_REFERENCE_KINDS = [ 'channel', 'warehouse', 'publication', 'option' ] as const;
export const AUTOMATION_DRAFT_REFERENCE_ROLES = [ 'binding', 'argument' ] as const;
export const AUTOMATION_DRAFT_REFERENCE_RESOLUTIONS = [ 'named', 'set' ] as const;

/**
 * `set.size` above this is sent as `'>cap'` — mirrors `RESOLVER_CANDIDATE_CAP`
 * in `teselly-ml-and-ai-library/src/automation/resolveReference.ts`.
 */
export const AUTOMATION_DRAFT_REFERENCE_SET_CAP = 20;

export type AutomationDraftReference = {
  referenceId: string;
  quote: string;
  kind: typeof AUTOMATION_DRAFT_REFERENCE_KINDS[number];
  role: typeof AUTOMATION_DRAFT_REFERENCE_ROLES[number];
  resolution: typeof AUTOMATION_DRAFT_REFERENCE_RESOLUTIONS[number];
  entities?: { id: string; name: string; fields?: Record<string, unknown> }[];
  /** `size` is the exact count as digits, or `'>cap'`. `channelIds` is never displayed. */
  set?: { scope: string; channelIds?: string[]; sample?: string[]; size: string };
  option?: { optionSet: string; value: string; label: string };
};

export type AutomationDraftSubGoal = {
  subGoalId: string;
  title: string;
  statement: string;
  display: { when: string; then: string };
  binding?: AutomationDraftBinding;
  clarifications: AutomationDraftClarification[];
  feasibility?: { verdict: 'likely' | 'unlikely' | 'unknown'; reasons: string[] };
  references?: AutomationDraftReference[];
};

export type AutomationDraftWire = {
  conversationId: string;
  goal: string;
  subGoals: AutomationDraftSubGoal[];
};

/**
 * What the card renders. `draftHash` is not decoration: Submit sends it back and
 * the server refuses a payload that does not match, so a card rendered without
 * one could never be submitted.
 */
export type AutomationDraftPayload = {
  draft: AutomationDraftWire;
  draftHash: string;
  /** Derived once here so the renderer does not re-derive it per sub-goal. */
  unresolvedCount: number;
};

const isRecord = ( value: unknown ): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isBinding = ( value: unknown ): value is AutomationDraftBinding =>
  isRecord( value ) &&
  ( value.kind === 'account-wide' || value.kind === 'enumerated' ) &&
  // Checked against the enum, not merely `typeof === 'string'`, for the same
  // reason `kind` is: a level this client does not know is a binding it cannot
  // faithfully submit, so it narrows away and the card reads as unresolved.
  AUTOMATION_DRAFT_HIERARCHY_LEVELS.some(( level ) => level === value.hierarchyLevel ) &&
  Array.isArray( value.hierarchyEntityIds ) &&
  typeof value.sellerQuote === 'string';

const isStringArray = ( value: unknown ): value is string[] =>
  Array.isArray( value ) && value.every(( v ) => typeof v === 'string' );

const isOneOf = <T extends string>( options: readonly T[], value: unknown ): value is T =>
  options.some(( option ) => option === value );

const isReferenceEntity = ( value: unknown ): boolean =>
  isRecord( value ) &&
  typeof value.id === 'string' &&
  typeof value.name === 'string' &&
  ( value.fields === undefined || ( isRecord( value.fields ) && !Array.isArray( value.fields )));

const isReferenceSet = ( value: unknown ): boolean =>
  isRecord( value ) &&
  typeof value.scope === 'string' &&
  typeof value.size === 'string' &&
  ( value.channelIds === undefined || isStringArray( value.channelIds )) &&
  ( value.sample === undefined || isStringArray( value.sample ));

const isReferenceOption = ( value: unknown ): boolean =>
  isRecord( value ) &&
  typeof value.optionSet === 'string' &&
  typeof value.value === 'string' &&
  typeof value.label === 'string';

const isReference = ( value: unknown ): value is AutomationDraftReference =>
  isRecord( value ) &&
  typeof value.referenceId === 'string' &&
  typeof value.quote === 'string' &&
  isOneOf( AUTOMATION_DRAFT_REFERENCE_KINDS, value.kind ) &&
  isOneOf( AUTOMATION_DRAFT_REFERENCE_ROLES, value.role ) &&
  isOneOf( AUTOMATION_DRAFT_REFERENCE_RESOLUTIONS, value.resolution ) &&
  ( value.entities === undefined || ( Array.isArray( value.entities ) && value.entities.every( isReferenceEntity ))) &&
  ( value.set === undefined || isReferenceSet( value.set )) &&
  ( value.option === undefined || isReferenceOption( value.option ));

/**
 * A malformed `references` fails the WHOLE sub-goal (and so the whole draft),
 * unlike a malformed `binding`, which narrows away to "unresolved". The asymmetry
 * is deliberate: an absent binding is a state the card can honestly show and the
 * server gate refuses on its own terms, whereas dropping references the server
 * HASHED guarantees a `DRAFT_HASH_MISMATCH` on a card that looked submittable.
 */
const isSubGoal = ( value: unknown ): value is AutomationDraftSubGoal =>
  isRecord( value ) &&
  typeof value.subGoalId === 'string' &&
  typeof value.statement === 'string' &&
  isRecord( value.display ) &&
  typeof ( value.display as Record<string, unknown> ).when === 'string' &&
  typeof ( value.display as Record<string, unknown> ).then === 'string' &&
  ( value.references === undefined || ( Array.isArray( value.references ) && value.references.every( isReference )));

/**
 * 🚨 An `enumerated` binding is a SNAPSHOT of the seller's catalogue at agreement
 * time. Items added later are not covered and nothing notifies her. The renderer
 * MUST surface this — it is the difference between a rule that quietly stops
 * applying to half her catalogue and one she knows the boundaries of.
 */
export const bindingIsSnapshot = ( binding?: AutomationDraftBinding ): boolean =>
  binding?.kind === 'enumerated';

/** Clarifications a sub-goal is still waiting on. Mirrors the server gate's predicate. */
export const unresolvedClarifications = ( subGoal: AutomationDraftSubGoal ): AutomationDraftClarification[] =>
  ( subGoal.clarifications ?? []).filter(( c ) => typeof c.answer !== 'string' || c.answer.trim() === '' );

/**
 * Everything still standing between this draft and a submittable one — the
 * client-side ECHO of the server gate, not a second gate. The server refuses
 * regardless; this only lets the card grey out Submit and say why instead of
 * offering a click that is going to come back refused.
 */
export const countUnresolved = ( draft: AutomationDraftWire ): number =>
  ( draft.subGoals ?? []).reduce(
    ( total, sg ) => total + unresolvedClarifications( sg ).length + ( sg.binding ? 0 : 1 ),
    0
  );

/**
 * Narrow the opaque tool result into a renderable payload.
 *
 * Shape-narrowing only, mirroring `toRulePreviewPayload`'s discipline: the
 * backend already decided what the draft is. Anything unrecognised returns
 * `undefined` and the message renders as plain text — a malformed payload must
 * never throw on the render path.
 *
 * A draft with NO sub-goals returns `undefined`: an empty card is not an
 * agreement, and the assistant's own message text is the honest answer on its own.
 * A missing `draftHash` also returns `undefined` — a card that cannot be
 * submitted is worse than no card, because it looks like progress.
 */
export const toAutomationDraftPayload = ( result: unknown ): AutomationDraftPayload | undefined => {
  if ( !isRecord( result )) return undefined;
  if ( result.ok !== true ) return undefined;

  const draft = result.draft;
  const draftHash = result.draftHash;
  if ( !isRecord( draft ) || typeof draftHash !== 'string' || !draftHash ) return undefined;
  if ( typeof draft.conversationId !== 'string' ) return undefined;
  if ( !Array.isArray( draft.subGoals ) || draft.subGoals.length === 0 ) return undefined;
  if ( !draft.subGoals.every( isSubGoal )) return undefined;

  const normalized: AutomationDraftWire = {
    conversationId: draft.conversationId,
    goal: typeof draft.goal === 'string' ? draft.goal : '',
    subGoals: ( draft.subGoals as AutomationDraftSubGoal[]).map(( sg ) => ({
      subGoalId: sg.subGoalId,
      title: typeof sg.title === 'string' ? sg.title : '',
      statement: sg.statement,
      display: { when: sg.display.when, then: sg.display.then },
      ...( isBinding( sg.binding ) ? { binding: sg.binding } : {}),
      clarifications: Array.isArray( sg.clarifications ) ? sg.clarifications : [],
      ...( isRecord( sg.feasibility ) ? { feasibility: sg.feasibility as AutomationDraftSubGoal['feasibility'] } : {}),
      // Forwarded AS RECEIVED, never re-projected: the server hashes them and
      // re-hashes what Submit sends, so unknown keys and `entities[].fields` must
      // survive. Absent stays absent — a pre-S1 draft must hash as before.
      ...( sg.references !== undefined ? { references: sg.references } : {})
    }))
  };

  return { draft: normalized, draftHash, unresolvedCount: countUnresolved( normalized ) };
};

/**
 * The body `POST /ai-assistant/automation-requests/submit` requires.
 *
 * All four fields are mandatory — `aiAssistant.endpoint.ts:157-162` declares
 * `S.shape({ conversationId, draftHash, draft, automationRequestId })` with no
 * `.optional()` on any of them, and the generated operation type mirrors that.
 * This local type is NOT the gate: the gate is passing the result to the typed
 * `backend.post.ai_assistant_submit_automation_request`, which is what fails to
 * compile if this drifts from the endpoint.
 */
export type SubmitAutomationRequestBody = {
  conversationId: string;
  draftHash: string;
  draft: AutomationDraftWire;
  automationRequestId: string;
};

/**
 * Build the submit body for an agreed draft. Pure — the caller owns the network
 * call and owns `automationRequestId`.
 *
 * `conversationId` comes from the DRAFT, never from whatever conversation happens
 * to be open: the draft is an agreement about the conversation it was proposed
 * in, and a rehydrated card in a second tab must still submit against that one.
 *
 * `automationRequestId` is a parameter rather than minted here because it becomes
 * the aggregate id of `SubmitAutomationRequest` — the caller mints it ONCE per
 * card so a retry resolves to the SAME Automation Request instead of a second one
 * (`teselly-ml-and-ai-library/src/capabilities/automation/submit_automation_request.ts`).
 *
 * `draft` is forwarded as-is: `toAutomationDraftPayload` already normalised it to
 * exactly the wire shape, so re-projecting here would only add a second place for
 * the shape to drift.
 */
export const toSubmitAutomationRequestBody = (
  payload: AutomationDraftPayload,
  automationRequestId: string
): SubmitAutomationRequestBody => ({
  conversationId: payload.draft.conversationId,
  draftHash: payload.draftHash,
  draft: payload.draft,
  automationRequestId
});

/**
 * The three outcomes a submit can have, kept APART because the card has to say a
 * different thing for each and two of them arrive down the same wire.
 *
 * `POST /ai-assistant/automation-requests/submit` returns **200 with the tool's
 * return value verbatim, refusals included** — `{ ok: false }` is the
 * discriminator, and a refusal is "a real answer the card renders back to the
 * seller ... not a transport error" (`aiAssistant.endpoint.ts`, the submit
 * endpoint's docblock). So a resolved promise is NOT the same as an accepted
 * submit, and only a THROW is a transport failure.
 *
 *  - `submitted` — the request now EXISTS. `url` is the handover link the server
 *    formatted (`defaultRequestUrlFor`, `submit_automation_request.ts:131-132`).
 *  - `refused`   — the server said no, in its own words. Retryable after she
 *    fixes what `message` names.
 *  - `failed`    — the call never produced an answer this client can read.
 */
export type SubmitAutomationRequestOutcome =
  | { kind: 'submitted'; url?: string }
  | { kind: 'refused'; message: string }
  | { kind: 'failed' };

/**
 * Characters that can turn a leading-`/` value into an off-site one: a backslash,
 * and every C0 control character plus DEL. See `isInAppPath`.
 */
const OFF_SITE_NORMALISING_CHARS = /[\\\u0000-\u001F\u007F]/;

/**
 * An in-app path, and nothing else. The link is rendered into an `href`, so an
 * absolute URL from the response would be an open redirect and a `javascript:`
 * one an XSS.
 *
 * A leading `/` is necessary but NOT sufficient, and `//` is NOT the only off-site
 * shape. Checked with the WHATWG URL parser against base
 * `https://app.teselly.com/chat`, every one of these resolves to
 * `https://evil.example/`:
 *
 *   `//evil.example`     protocol-relative;
 *   `/\evil.example`     a browser normalises `\` to `/` in the authority position;
 *   `/<TAB>/evil.example`, `/<LF>/evil.example`, `/<CR>/evil.example` — TAB, LF and
 *   CR are STRIPPED anywhere in a URL before it is parsed, so each of these IS
 *   `//evil.example` by the time it is resolved.
 *
 * So the predicate demands a leading `/`, rejects a second `/` immediately after
 * it, and rejects a backslash or any C0 control character (U+0000–U+001F) or DEL
 * (U+007F) ANYWHERE in the value. `defaultRequestUrlFor` produces
 * `/automation/requests/<id>` (`submit_automation_request.ts:130-132`), which
 * contains none of them.
 *
 * Deliberately a character rule rather than a regex pinned to that route:
 * `requestUrlFor` is injectable (`SubmitAutomationRequestToolDeps`,
 * `submit_automation_request.ts:127`) precisely so TV1-2385/H can change the
 * route, and a shape pin would make that change drop the link silently instead of
 * rendering it.
 */
const isInAppPath = ( value: unknown ): value is string =>
  typeof value === 'string' &&
  value.startsWith( '/' ) &&
  !value.startsWith( '//' ) &&
  !OFF_SITE_NORMALISING_CHARS.test( value );

/**
 * Narrow the submit response into what the card must say.
 *
 * The generated operation types the 200 body as `{ [key: string]: unknown }` —
 * the endpoint declares `S.shape({}).additionalProperties( true )` — so this is
 * the only place the discriminator is read, and it is read defensively:
 *
 *  - `ok === true` without a usable `url` still means the request EXISTS. The
 *    honesty copy must flip; only the link is dropped.
 *  - `ok === false` without a `message` has no server words to show, so it
 *    degrades to `failed` rather than rendering an empty refusal.
 *  - anything with NO `ok` at all is `failed`. Treating an unrecognised 200 as
 *    acceptance is the one error that tells the seller something exists when it
 *    may not; offering the click again is safe, because the request id is minted
 *    once per card and a repeat submit is a no-op at the aggregate.
 */
export const toSubmitAutomationRequestOutcome = ( response: unknown ): SubmitAutomationRequestOutcome => {
  if ( !isRecord( response )) return { kind: 'failed' };

  if ( response.ok === true ) {
    return isInAppPath( response.url )
      ? { kind: 'submitted', url: response.url }
      : { kind: 'submitted' };
  }

  if ( response.ok === false && typeof response.message === 'string' && response.message.trim() !== '' ) {
    return { kind: 'refused', message: response.message };
  }

  return { kind: 'failed' };
};
